import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { listRoles, getRole, createRole, updateRole, deleteRole } from './role-client';
import type { CreateRoleBody, UpdateRoleBody } from './role-schemas';

/* ------------------------------------------------------------------ */
/*  Query keys                                                         */
/* ------------------------------------------------------------------ */

export const roleKeys = {
  all: (orgId: string) => ['roles', { orgId }] as const,
  detail: (orgId: string, roleKey: string) => ['roles', { orgId, roleKey }] as const,
};

/* ------------------------------------------------------------------ */
/*  Queries                                                            */
/* ------------------------------------------------------------------ */

export function useRoles(orgId: string) {
  return useQuery({
    queryKey: roleKeys.all(orgId),
    queryFn: () => listRoles(orgId),
    enabled: !!orgId,
  });
}

export function useRoleDetail(orgId: string, roleKey: string) {
  return useQuery({
    queryKey: roleKeys.detail(orgId, roleKey),
    queryFn: () => getRole(orgId, roleKey),
    enabled: !!orgId && !!roleKey,
  });
}

/* ------------------------------------------------------------------ */
/*  Mutations                                                          */
/* ------------------------------------------------------------------ */

export function useCreateRole() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ orgId, body }: { orgId: string; body: CreateRoleBody }) =>
      createRole(orgId, body),
    onSuccess: (_data, { orgId }) => {
      qc.invalidateQueries({ queryKey: roleKeys.all(orgId) });
    },
  });
}

export function useUpdateRole() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      orgId,
      roleKey,
      body,
    }: {
      orgId: string;
      roleKey: string;
      body: UpdateRoleBody;
    }) => updateRole(orgId, roleKey, body),
    onSuccess: (_data, { orgId, roleKey }) => {
      qc.invalidateQueries({ queryKey: roleKeys.all(orgId) });
      qc.invalidateQueries({ queryKey: roleKeys.detail(orgId, roleKey) });
    },
  });
}

export function useDeleteRole() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      orgId,
      roleKey,
      reassignTo,
    }: {
      orgId: string;
      roleKey: string;
      reassignTo?: string;
    }) => deleteRole(orgId, roleKey, reassignTo),
    onSuccess: (_data, { orgId }) => {
      qc.invalidateQueries({ queryKey: roleKeys.all(orgId) });
    },
  });
}
